import { getVerdictColor, formatPrice } from "@/lib/utils-app";
import { useState, useEffect } from "react";
import {
  Tag,
  Timer,
  Storefront,
  CalendarBlank,
  Fire,
  Eye,
  CaretDown,
  CaretUp,
  ClipboardText,
  ArrowsClockwise,
  Spinner,
  PencilSimple,
  Warning,
  ShieldCheck,
} from "@phosphor-icons/react";
import ListingTemplate from "@/components/app/ListingTemplate";
import ListOnEbayButton from "@/components/app/ListOnEbayButton";
import PhotoUpload from "@/components/app/PhotoUpload";
import CorrectListingModal from "@/components/app/CorrectListingModal";
import { imageAPI, verdictAPI } from "@/lib/api";

function DataCell({ label, value, icon: Icon }) {
  return (
    <div className="border-2 border-black p-3 flex flex-col gap-1" data-testid={`verdict-cell-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={14} weight="bold" className="text-neutral-500" />}
        <span className="text-xs font-body font-semibold tracking-[0.1em] uppercase text-neutral-500">
          {label}
        </span>
      </div>
      <span className="font-heading text-lg font-bold tracking-tight">{value}</span>
    </div>
  );
}

export default function VerdictCard({ verdict: initialVerdict }) {
  const [verdict, setVerdict] = useState(initialVerdict);
  const [expanded, setExpanded] = useState(false);
  const [showListing, setShowListing] = useState(false);
  const [photos, setPhotos] = useState(initialVerdict.photos || []);
  const [hasNewPhotos, setHasNewPhotos] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [rescanned, setRescanned] = useState(false);
  const [showCorrect, setShowCorrect] = useState(false);
  const [analysisPending, setAnalysisPending] = useState(!!initialVerdict.analysis_pending);
  const [retryingMarket, setRetryingMarket] = useState(false);
  const colors = getVerdictColor(verdict.verdict);

  // Poll for detailed analysis when pending
  useEffect(() => {
    if (!analysisPending || !verdict.id) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await verdictAPI.analysisStatus(verdict.id);
        if (!data.pending) {
          setVerdict((prev) => ({ ...prev, detailed_analysis: data.detailed_analysis, analysis_pending: false }));
          setAnalysisPending(false);
        }
      } catch {
        // silent — retry on next interval
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [analysisPending, verdict.id]);

  // Load photos from server if verdict has an id
  useEffect(() => {
    if (verdict.id && !initialVerdict.photos) {
      imageAPI.getPhotos(verdict.id).then(({ data }) => {
        if (data.photos?.length) setPhotos(data.photos);
      }).catch(() => {});
    }
  }, [verdict.id]);

  const handlePhotosChange = (newPhotos) => {
    const hadPhotos = photos.length;
    setPhotos(newPhotos);
    if (newPhotos.length > hadPhotos) {
      setHasNewPhotos(true);
      setRescanned(false);
    }
  };

  const handleRescan = async () => {
    setRescanning(true);
    try {
      const { data } = await verdictAPI.rescan(verdict.id);
      setVerdict((prev) => ({ ...data, photos }));
      // Rescan now also returns analysis_pending — start polling
      if (data.analysis_pending) {
        setAnalysisPending(true);
      }
      setRescanned(true);
      setHasNewPhotos(false);
    } catch {
      setRescanned(true);
    } finally {
      setRescanning(false);
    }
  };

  const handleRetryMarket = async () => {
    setRetryingMarket(true);
    try {
      const { data } = await verdictAPI.retryMarket(verdict.id);
      setVerdict((prev) => ({ ...prev, ...data, photos }));
    } catch {
      // silent
    } finally {
      setRetryingMarket(false);
    }
  };

  // Determine market data availability
  const marketStatus = verdict.market_data_status || (verdict.market_data?.data_driven ? "available" : "unavailable");
  const marketUnavailable = marketStatus === "unavailable";
  const marketPartial = marketStatus === "partial";

  // Use live sold avg as suggested list price when available
  const rawPrice = verdict.market_data?.sold_median_price > 0
    ? verdict.market_data.sold_median_price
    : (verdict.list_price_low || verdict.list_price_high || 0);
  const suggestedPrice = formatPrice(rawPrice);

  // Estimate shipping based on category/weight
  const category = (verdict.category || "clothing").toLowerCase();
  const estimatedShipping = category === "shoes" ? 12.99 : 6.49;
  const freeShippingPrice = formatPrice(rawPrice + estimatedShipping);
  const minOfferRaw = (rawPrice + estimatedShipping) * 0.7;
  const minimumOffer = formatPrice(minOfferRaw);

  // Acquisition cost and profit range
  const acquireLow = rawPrice * 0.10;
  const acquireHigh = rawPrice * 0.15;
  const profitLow = (minOfferRaw / 2) - acquireHigh;
  const profitHigh = rawPrice / 2;

  return (
    <div
      className="border-4 border-black bg-white shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] overflow-hidden"
      data-testid="verdict-card"
    >
      {/* Verdict Header */}
      <div
        className="p-5 border-b-4 border-black"
        style={{ backgroundColor: colors.bg, color: colors.text }}
        data-testid="verdict-header"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="font-heading text-5xl font-black tracking-tighter uppercase">
            {verdict.verdict}
          </div>
          <div className={`mt-1 px-2 py-1 border-2 font-body text-xs font-bold uppercase tracking-wide shrink-0 ${
            marketUnavailable
              ? "border-red-400 bg-red-50 text-red-700"
              : marketPartial
                ? "border-amber-400 bg-amber-50 text-amber-700"
                : verdict.market_data?.data_driven
                  ? "border-black bg-white text-black"
                  : "border-current bg-transparent"
          }`} data-testid="verdict-source-badge">
            {marketUnavailable ? "AI Estimate" : marketPartial ? "Partial Data" : verdict.market_data?.data_driven ? "Live Data" : "AI Estimate"}
          </div>
        </div>
        {verdict.override_reason && (
          <div className="mt-1 font-body text-sm font-medium opacity-80">
            {verdict.override_reason}
          </div>
        )}
      </div>

      {/* Title + Thumbnail */}
      <div className="p-4 border-b-2 border-black flex items-start gap-3">
        {verdict.thumbnail && verdict.thumbnail.length > 10 && (
          <div className="w-16 h-16 border-2 border-black shrink-0 overflow-hidden">
            <img
              src={`data:image/jpeg;base64,${verdict.thumbnail}`}
              alt=""
              className="w-full h-full object-cover"
              data-testid="verdict-thumbnail"
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-body text-xs font-semibold tracking-[0.1em] uppercase text-neutral-500 mb-1">
            Listing Title
          </div>
          <div className="font-heading text-lg font-bold tracking-tight" data-testid="verdict-title">
            {verdict.title_keywords}
          </div>
        </div>
      </div>

      {/* Data Grid */}
      <div className="grid grid-cols-2">
        <DataCell label="Suggested List Price" value={suggestedPrice} icon={Tag} />
        <DataCell label="Time to Sell" value={verdict.time_to_sell} icon={Timer} />
        <DataCell label="Free Shipping Price" value={freeShippingPrice} icon={Tag} />
        <DataCell label="Minimum Offer" value={minimumOffer} icon={Tag} />
        <DataCell label="Acquire Between" value={rawPrice > 0 ? `${formatPrice(acquireLow)} - ${formatPrice(acquireHigh)}` : "—"} icon={Tag} />
        <DataCell label="Profit Range" value={rawPrice > 0 ? `${formatPrice(profitLow)} - ${formatPrice(profitHigh)}` : "—"} icon={Tag} />
        <DataCell label="Competition" value={verdict.active_listings} icon={Storefront} />
        <DataCell label="Season" value={verdict.season} icon={CalendarBlank} />
        <DataCell label="Platform" value={verdict.best_platform} icon={Storefront} />
        <DataCell label="Demand" value={verdict.demand} icon={Fire} />
      </div>

      {/* Market Research Tiers */}
      {(() => {
        const tiers = verdict.search_tiers || [];
        const md = verdict.market_data || {};

        // If we have tier data, show the full tiered view
        if (tiers.length > 0) {
          return (
            <div className="border-t-2 border-black" data-testid="market-tiers-section">
              <div className="px-4 py-2.5 bg-neutral-100 flex items-center justify-between">
                <div className="font-heading text-[10px] font-bold uppercase tracking-widest text-neutral-500">Market Research</div>
                {(marketUnavailable || marketPartial) && verdict.id && (
                  <button
                    onClick={handleRetryMarket}
                    disabled={retryingMarket}
                    className="px-2 py-0.5 bg-black text-white font-heading text-[9px] font-bold uppercase tracking-wider border border-black disabled:opacity-50 flex items-center gap-1"
                    data-testid="retry-market-btn"
                  >
                    {retryingMarket ? <Spinner size={10} weight="bold" className="animate-spin" /> : <ArrowsClockwise size={10} weight="bold" />}
                    {retryingMarket ? "Retrying" : "Retry"}
                  </button>
                )}
              </div>
              {tiers.map((tier, i) => {
                const hasData = tier.active_count > 0 || tier.sold_count > 0;
                const strText = tier.str != null
                  ? `${tier.sold_hit_cap ? tier.str + "%+" : tier.str + "%"}`
                  : "—";
                const strColor = tier.str >= 50
                  ? "text-green-700 bg-green-50"
                  : tier.str >= 20
                    ? "text-blue-700 bg-blue-50"
                    : tier.str >= 10
                      ? "text-amber-700 bg-amber-50"
                      : tier.str != null
                        ? "text-red-700 bg-red-50"
                        : "text-neutral-400 bg-neutral-50";
                const isPrimary = tier.label === "Core Item";
                const ebayQ = encodeURIComponent(tier.query);
                const soldLink = `https://www.ebay.com/sch/i.html?_nkw=${ebayQ}&LH_Sold=1&LH_Complete=1`;
                const activeLink = `https://www.ebay.com/sch/i.html?_nkw=${ebayQ}`;
                return (
                  <div
                    key={i}
                    className={`px-4 py-2.5 border-t border-neutral-200 ${isPrimary ? "bg-black/[0.03] border-l-4 border-l-black" : ""}`}
                    data-testid={`market-tier-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`font-heading text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 ${isPrimary ? "bg-black text-white" : "bg-neutral-200 text-neutral-600"}`}>
                        {tier.label}
                      </span>
                      {tier.sold_median > 0 && (
                        <span className="font-body text-[10px] text-neutral-500 ml-auto">
                          Median ${Math.floor(tier.sold_median)}
                        </span>
                      )}
                    </div>
                    <a
                      href={soldLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-body text-[11px] text-blue-600 underline underline-offset-2 truncate block mb-2"
                      title={`View eBay sold results for: ${tier.query}`}
                      data-testid={`market-tier-${i}-link`}
                    >
                      "{tier.query}"
                    </a>
                    {hasData ? (
                      <div className="flex items-center gap-3">
                        <div className={`font-heading text-base font-black px-2 py-0.5 ${strColor}`}>
                          {strText}
                        </div>
                        <div className="flex items-center gap-2 font-body text-xs text-neutral-600">
                          <a href={soldLink} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 hover:underline">
                            {tier.sold_hit_cap ? `${tier.sold_count.toLocaleString()}+` : tier.sold_count.toLocaleString()} sold
                          </a>
                          <span className="text-neutral-300">/</span>
                          <a href={activeLink} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 hover:underline">
                            {tier.active_count.toLocaleString()} active
                          </a>
                        </div>
                      </div>
                    ) : (
                      <div className="font-body text-[11px] text-neutral-400">No data</div>
                    )}
                    {tier.sold_hit_cap && (
                      <div className="font-body text-[10px] text-amber-600 mt-1">Sample capped — actual sold count is higher</div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        }

        // Fallback: old-style display for verdicts without tier data
        const soldCount = md.sold_count || 0;
        const activeCount = md.active_count || 0;
        const str = md.sell_through_rate;
        const hasSold = soldCount > 0;
        const hasActive = activeCount > 0;

        if (hasSold || hasActive) {
          return (
            <div className="border-t-2 border-black bg-neutral-50 px-4 py-3" data-testid="market-stats-section">
              {str != null ? (
                <div className="flex items-center gap-3">
                  <div className="font-heading text-2xl font-black">{str}%</div>
                  <div>
                    <div className="font-body text-xs font-semibold tracking-[0.1em] uppercase text-neutral-500">Sell-Through Rate</div>
                    <div className="font-body text-xs text-neutral-500">{soldCount.toLocaleString()} sold / {activeCount.toLocaleString()} active</div>
                  </div>
                </div>
              ) : (
                <div className="font-body text-xs text-neutral-500">
                  {hasSold && <span>{soldCount.toLocaleString()} sold</span>}
                  {hasSold && hasActive && <span> / </span>}
                  {hasActive && <span>{activeCount.toLocaleString()} active</span>}
                </div>
              )}
            </div>
          );
        }

        // Fully unavailable
        if (marketUnavailable && verdict.id) {
          return (
            <div className="px-4 py-3 border-t-2 border-black bg-amber-50/60 flex items-center justify-between gap-2" data-testid="market-data-status">
              <div className="flex items-center gap-2 min-w-0">
                <Warning size={16} weight="fill" className="text-amber-600 shrink-0" />
                <span className="font-body text-xs text-amber-800">Market data unavailable — using AI estimate</span>
              </div>
              <button
                onClick={handleRetryMarket}
                disabled={retryingMarket}
                className="shrink-0 px-3 py-1.5 bg-black text-white font-heading text-[10px] font-bold uppercase tracking-wider border-2 border-black disabled:opacity-50 flex items-center gap-1.5"
                data-testid="retry-market-btn"
              >
                {retryingMarket ? <><Spinner size={12} className="animate-spin" /> Retrying</> : <><ArrowsClockwise size={12} /> Retry</>}
              </button>
            </div>
          );
        }

        return null;
      })()}

      {/* Counterfeit Risk Badge */}
      {verdict.counterfeit_risk && verdict.counterfeit_risk !== "LOW" && (
        <div className={`px-4 py-3 border-t-2 border-black flex items-start gap-2 ${
          verdict.counterfeit_risk === "HIGH" ? "bg-red-50" : "bg-amber-50"
        }`} data-testid="counterfeit-risk-badge">
          <Warning size={18} weight="fill" className={verdict.counterfeit_risk === "HIGH" ? "text-red-600 shrink-0 mt-0.5" : "text-amber-600 shrink-0 mt-0.5"} />
          <div>
            <div className={`font-heading text-xs font-bold uppercase tracking-wider ${
              verdict.counterfeit_risk === "HIGH" ? "text-red-700" : "text-amber-700"
            }`}>
              {verdict.counterfeit_risk} Counterfeit Risk
            </div>
            {verdict.counterfeit_flags && (
              <div className="font-body text-xs text-neutral-600 mt-0.5">{verdict.counterfeit_flags}</div>
            )}
          </div>
        </div>
      )}
      {verdict.counterfeit_risk === "LOW" && (
        <div className="px-4 py-2 border-t-2 border-black flex items-center gap-1.5 bg-green-50" data-testid="counterfeit-risk-badge">
          <ShieldCheck size={16} weight="fill" className="text-green-600" />
          <span className="font-body text-xs font-semibold text-green-700">Authentic Indicators</span>
        </div>
      )}

      {/* Condition */}
      <div className="p-4 border-t-2 border-black">
        <div className="flex items-center gap-1.5 mb-1">
          <Eye size={14} weight="bold" className="text-neutral-500" />
          <span className="text-xs font-body font-semibold tracking-[0.1em] uppercase text-neutral-500">
            Condition
          </span>
        </div>
        <div className="font-body text-sm" data-testid="verdict-condition">{verdict.condition}</div>
      </div>

      {/* Action Buttons Row */}
      <div className="grid grid-cols-2 border-t-2 border-black">
        <button
          onClick={() => { setExpanded(!expanded); if (showListing) setShowListing(false); }}
          className="p-3 flex items-center justify-center gap-1.5 font-body font-semibold text-xs uppercase tracking-[0.1em] hover:bg-neutral-100 transition-colors border-r-2 border-black"
          data-testid="verdict-details-toggle"
        >
          {expanded ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
          {expanded ? "Hide" : "Details"}
        </button>
        <button
          onClick={() => { setShowListing(!showListing); if (expanded) setExpanded(false); }}
          className={`p-3 flex items-center justify-center gap-1.5 font-body font-semibold text-xs uppercase tracking-[0.1em] transition-colors ${
            showListing ? "bg-black text-white" : "hover:bg-neutral-100"
          }`}
          data-testid="verdict-listing-toggle"
        >
          <ClipboardText size={12} weight="bold" />
          {showListing ? "Hide" : "List It"}
        </button>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="p-4 border-t-2 border-black bg-neutral-50" data-testid="verdict-details-expanded">
          <div className="font-body text-sm leading-relaxed">
            {analysisPending ? (
              <div className="flex items-center gap-2 text-neutral-500" data-testid="analysis-loading">
                <Spinner size={16} weight="bold" className="animate-spin" />
                <span>Generating detailed analysis...</span>
              </div>
            ) : (
              verdict.detailed_analysis
            )}
          </div>
          {verdict.data_verdict_reason && verdict.data_verdict_reason !== "Insufficient market data — using AI estimate" && (
            <div className="mt-3 p-3 border-2 border-black bg-white">
              <div className="font-body text-xs font-semibold tracking-[0.1em] uppercase text-neutral-500 mb-1">Market Analysis</div>
              <div className="font-body text-xs text-neutral-600">{verdict.data_verdict_reason}</div>
            </div>
          )}
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-body">
            <div><span className="font-semibold">Brand:</span> {verdict.brand}</div>
            <div><span className="font-semibold">Size:</span> {verdict.size}</div>
            <div><span className="font-semibold">Style:</span> {verdict.style || verdict.category}</div>
            <div><span className="font-semibold">NWT:</span> {verdict.is_nwt ? "Yes" : "No"}</div>
          </div>
        </div>
      )}

      {/* Listing Template Panel */}
      {showListing && (
        <div className="p-4 border-t-2 border-black space-y-4" data-testid="verdict-listing-panel">
          <PhotoUpload verdictId={verdict.id} photos={photos} onPhotosChange={handlePhotosChange} />

          {/* Correct this listing */}
          <button
            onClick={() => setShowCorrect(true)}
            className="w-full p-2.5 bg-white text-black font-heading font-bold uppercase tracking-wide text-xs border-2 border-black flex items-center justify-center gap-2 hover:bg-neutral-50 active:translate-y-[1px] transition-all"
            data-testid="verdict-correct-btn"
          >
            <PencilSimple size={14} weight="bold" />
            Correct This Listing
          </button>

          {/* eBay listing flow: upload photos → rescan → list */}
          {verdict.id && (
            <div className="space-y-2">
              {photos.length === 0 ? (
                <p className="font-body text-xs text-neutral-500 text-center py-1">Upload listing photos above to enable eBay listing</p>
              ) : hasNewPhotos && !rescanned ? (
                <button
                  onClick={handleRescan}
                  disabled={rescanning}
                  className="w-full p-3 bg-black text-white font-heading font-bold uppercase tracking-wide text-sm border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:shadow-none transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="rescan-btn"
                >
                  {rescanning ? (
                    <><Spinner size={16} weight="bold" className="animate-spin" /> Updating listing details...</>
                  ) : (
                    <><ArrowsClockwise size={16} weight="bold" /> Update listing with new photos</>
                  )}
                </button>
              ) : (
                <ListOnEbayButton verdict={{...verdict, photos}} />
              )}
            </div>
          )}

          {/* Copy & paste templates — collapsed by default */}
          <details className="group" data-testid="listing-templates">
            <summary className="font-heading text-xs font-bold uppercase tracking-wider text-neutral-500 cursor-pointer flex items-center gap-1 py-2 border-t border-neutral-200">
              <CaretDown size={12} weight="bold" className="group-open:rotate-180 transition-transform" />
              Copy & Paste Templates
            </summary>
            <div className="pt-2">
              <ListingTemplate verdict={verdict} />
            </div>
          </details>
        </div>
      )}

      {/* Correct Listing Modal */}
      {showCorrect && (
        <CorrectListingModal
          verdict={verdict}
          onClose={() => setShowCorrect(false)}
          onCorrected={(updated) => {
            setVerdict((prev) => ({ ...prev, ...updated, photos }));
          }}
        />
      )}
    </div>
  );
}
